import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Row, Col, Button } from 'reactstrap';
import { Document as ReactPdfDocument, Page as ReactPdfPage, pdfjs } from 'react-pdf';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { processSignaturePosition } from "./utils"

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

import './style.css';

import { SIGNATURE_RECTANGLE } from './constants';

const { WIDTH: rectangleWidth, HEIGHT: rectangleHeight } = SIGNATURE_RECTANGLE;

const SCALE = 1.0;

const SignerCoordinatesConfigurationSelector = (props) => {

  const {
    resource,
    signers,
    currentSignerCustomId,
    callbacks: {
      onSetResource: onSetResourceCallback,
      onChangeCurrentSignerCustomerId: onChangeCurrentSignerCustomerIdCallback
    }
  } = props;

  const {
    document_information: {
      pages: documentPages
    }
  } = resource;

  const pdfPageRef = useRef(null);

  const [currentPage, setCurrentPage] = useState({
    number: 1,
    dimensions: {
      width: 0,
      height: 0
    },
    page: null
  });

  const currentPageNumber = currentPage.number;

  const fileUrl = useMemo(() => {
    return URL.createObjectURL(resource.file);
  }, [resource.file]);

  useEffect(() => {
    return () => {
      // Limpiar la URL cuando el componente se desmonte
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  const onPageRenderSuccess = (page) => {
    const pageElement = pdfPageRef.current.pageElement.current;
    const canvasElement = _.find(pageElement.children, (child) => child.tagName === 'CANVAS');
    const parentElement = canvasElement.parentElement;

    const rectCanvas = canvasElement.getBoundingClientRect();
    const rectParent = parentElement.getBoundingClientRect();

    const x = rectCanvas.left - rectParent.left;
    const y = rectCanvas.top - rectParent.top;

    const width = rectCanvas.width;
    const height = rectCanvas.height;

    setCurrentPage({
      number: currentPage.number,
      coordinates: {
        x,
        y,
      },
      dimensions: {
        width,
        height
      },
      page: page
    });
  };

  const [{ }, drop] = useDrop({
    accept: 'signature-rectangle',
    drop: (item, monitor) => {
      const signer = _.find(signers, (signer) => signer.custom_id === item.id);

      if (!signer) return;

      const delta = monitor.getDifferenceFromInitialOffset();
      const { x, y } = signer.sign_coordinates;

      const { x: adjustedX, y: adjustedY } = processSignaturePosition({
        currentX: x,
        currentY: y,
        deltaX: delta.x,
        deltaY: -delta.y,
        width: rectangleWidth,
        height: rectangleHeight,
        pageDimensions: currentPage.dimensions,
        existingSignatures: documentPages[currentPageNumber - 1]?.signatures || [],
        signers: _.filter(signers, (signer) => signer.custom_id !== item.id)
      });

      onSetResourceCallback(prevState => {
        const _signers = _.map(prevState.signers, (signer) => {
          if (signer.custom_id === item.id) {
            signer.sign_coordinates = { ...signer.sign_coordinates, x: adjustedX, y: adjustedY };
          }
          return signer;
        })

        return {
          ...prevState,
          signers: _signers
        }
      });
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  });


  const convertToPdfPoint = (x, y) => {
    if (!currentPage.page) return { x, y };

    const viewport = currentPage.page.getViewport({ scale: SCALE });

    const [viewportX, viewportY] = viewport.convertToPdfPoint(x, y);
    return {
      x: viewportX,
      y: viewportY
    };
  };

  const renderContainerSignatureRectangles = () => {
    const currentSigners = _.filter(signers, (signer) => signer?.sign_coordinates?.page_number === currentPageNumber);
    const existingSignatures = documentPages[currentPageNumber - 1]?.signatures || [];

    if (pdfPageRef.current) {
      return (
        <div
          className="container-signature-rectangles"
          ref={drop}
          style={{
            width: currentPage.dimensions.width,
            height: currentPage.dimensions.height,
            top: currentPage.coordinates.y,
            position: 'absolute',
            cursor: currentSignerCustomId ? 'crosshair' : 'default',
          }}
          onClick={event => {
            if (currentSignerCustomId) {
              const signer = _.find(signers, (signer) => signer.custom_id === currentSignerCustomId);

              const rect = event.currentTarget.getBoundingClientRect();
              const x = event.clientX - rect.left;
              const y = event.clientY - rect.top;
              const { x: pdfX, y: pdfY } = convertToPdfPoint(x, y);

              const { x: adjustedX, y: adjustedY } = processSignaturePosition({
                currentX: signer.sign_coordinates.x,
                currentY: signer.sign_coordinates.y,
                deltaX: pdfX - signer.sign_coordinates.x,
                deltaY: pdfY - signer.sign_coordinates.y,
                width: rectangleWidth,
                height: rectangleHeight,
                pageDimensions: currentPage.dimensions,
                existingSignatures: existingSignatures,
                signers: _.filter(signers, (signer) => signer.custom_id !== currentSignerCustomId)
              });

              onSetResourceCallback(prevState => {
                const _signers = _.map(prevState.signers, (signer) => {
                  if (signer.custom_id === currentSignerCustomId) {
                    signer.sign_coordinates = { ...signer.sign_coordinates, x: adjustedX, y: adjustedY, page_number: currentPageNumber };
                  }
                  return signer;
                })

                return {
                  ...prevState,
                  signers: _signers
                }
              });

              onChangeCurrentSignerCustomerIdCallback(null);
            }
          }}
        >
          {_.map(existingSignatures, (signature, index) => {
            const [left, bottom, right, top] = signature.coordinates;
            const width = right - left;
            const height = top - bottom;

            return (
              <SignerCoordinatesConfigurationSignRectangle
                key={`signature-${index}`}
                signer={{
                  custom_id: `static-${index}`,
                  sign_coordinates: { x: left, y: bottom, page_number: currentPageNumber },
                  name: '',
                  email: '',
                  role: '',
                  color: 'rgba(0, 0, 255, 0.2)',
                  order: index,
                }}
                currentPage={currentPage}
                isStatic={true}
                width={width}
                height={height}
              />
            );
          })}
          {_.map(currentSigners, (signer) => {
            return (
              <SignerCoordinatesConfigurationSignRectangle
                key={signer.custom_id}
                signer={signer}
                currentPage={currentPage}
              />
            );
          })}
        </div>
      );
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Row>
        <Col md={12} className='text-center'>
          <ReactPdfDocument
            file={fileUrl}
          >
            <ReactPdfPage
              pageNumber={currentPageNumber}
              renderTextLayer={false}
              ref={pdfPageRef}
              onRenderSuccess={onPageRenderSuccess}
              renderAnnotationLayer={false}
            />
            {renderContainerSignatureRectangles()}
          </ReactPdfDocument>

          <div className="mt-3 text-center">
            <div className="btn-group">
              <Button
                color="secondary"
                onClick={() => setCurrentPage({ ...currentPage, number: 1 })}
                disabled={currentPageNumber <= 1}
                className="btn-sm"
              >
                {`<<`}
              </Button>
              <Button
                color="secondary"
                onClick={() => setCurrentPage({ ...currentPage, number: currentPageNumber - 1 })}
                disabled={currentPageNumber <= 1}
                className="btn-sm"
              >
                {`<`}
              </Button>
              <span className="mx-2">Página {currentPageNumber} de {documentPages.length}</span>
              <Button
                color="secondary"
                onClick={() => setCurrentPage({ ...currentPage, number: currentPageNumber + 1 })}
                disabled={currentPageNumber >= documentPages.length}
                className="btn-sm"
              >
                {`>`}
              </Button>
              <Button
                color="secondary"
                onClick={() => setCurrentPage({ ...currentPage, number: documentPages.length })}
                disabled={currentPageNumber >= documentPages.length}
                className="btn-sm"
              >
                {`>>`}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </DndProvider>
  )
};

const SignerCoordinatesConfigurationSignRectangle = (props) => {

  const {
    signer,
    currentPage,
    width = rectangleWidth,
    height = rectangleHeight
  } = props;

  const convertCoordinates = (x, y) => {
    if (!currentPage.page) return { x, y };

    const viewport = currentPage.page.getViewport({ scale: SCALE });

    const [viewportX, viewportY] = viewport.convertToViewportPoint(x, y);
    return {
      x: viewportX,
      y: viewportY
    };
  };

  const { x, y } = convertCoordinates(
    signer.sign_coordinates.x,
    signer.sign_coordinates.y
  );

  const [{ isDragging }, drag] = useDrag({
    type: 'signature-rectangle',
    item: { id: signer.custom_id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: !props.isStatic
  });


  return (
    <div
      ref={drag}
      className="signature-rectangle text-center rounded"
      style={{
        left: x,
        top: y - height,
        position: 'absolute',
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: signer.color,
        cursor: props.isStatic ? 'default' : 'move',
        opacity: isDragging ? 0.5 : 0.9,
        border: '1px solid #000',
        fontSize: '10px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: '2px',
        boxSizing: 'border-box'
      }}
    >
      <strong>{signer.email}</strong>
      <div>
        <span className="d-block">{signer.name}</span>
        <span className="d-block">{signer.role}</span>
      </div>
    </div>
  )
}

export default SignerCoordinatesConfigurationSelector;
